import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuthStore } from '../store/authStore';

const difficulties = [
  {
    id: 'beginner',
    name: '初学者',
    description: '4x4 迷你数独，适合新手入门',
    color: 'from-pink-400 to-rose-400',
    emoji: '🌸',
  },
  {
    id: 'easy',
    name: '简单',
    description: '标准数独，帮助你熟悉规则',
    color: 'from-green-500 to-emerald-600',
    emoji: '🌱',
  },
  {
    id: 'medium',
    name: '中等',
    description: '需要一定技巧，适合有经验的玩家',
    color: 'from-yellow-500 to-orange-600',
    emoji: '⚡',
  },
  {
    id: 'hard',
    name: '困难',
    description: '极具挑战性，只有大师才能征服',
    color: 'from-red-500 to-rose-600',
    emoji: '🔥',
  },
];

export default function HomePage() {
  const { user, isAuthenticated } = useAuthStore();

  return (
    <div className="py-4 sm:py-8">
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-6 sm:mb-12 px-2"
      >
        <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold mb-3 sm:mb-4">
          <span className="bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500 bg-clip-text text-transparent">
            疯狂的数独
          </span>
        </h1>
        <p className="text-base sm:text-xl text-gray-500 max-w-2xl mx-auto px-2">
          挑战你的逻辑思维，享受解谜的乐趣。从初学者到困难，找到属于你的挑战！
        </p>
      </motion.div>

      {/* User Welcome */}
      {isAuthenticated && user && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="glass-card p-4 sm:p-6 mb-6 sm:mb-8 text-center"
        >
          <p className="text-base sm:text-lg">
            欢迎回来，<span className="font-semibold text-green-600">{user.username}</span>！
          </p>
          <div className="flex justify-center gap-4 sm:gap-8 mt-3 sm:mt-4 text-xs sm:text-sm text-gray-500">
            <div>
              <span className="text-xl sm:text-2xl font-bold text-gray-800">{user.totalGamesCompleted}</span>
              <p>完成游戏</p>
            </div>
            <div>
              <span className="text-xl sm:text-2xl font-bold text-green-500">{user.easyCompleted}</span>
              <p>简单</p>
            </div>
            <div>
              <span className="text-xl sm:text-2xl font-bold text-yellow-500">{user.mediumCompleted}</span>
              <p>中等</p>
            </div>
            <div>
              <span className="text-xl sm:text-2xl font-bold text-red-500">{user.hardCompleted}</span>
              <p>困难</p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Difficulty Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8 sm:mb-12">
        {difficulties.map((diff, index) => (
          <motion.div
            key={diff.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 * (index + 1) }}
          >
            <Link
              to={`/levels/${diff.id}`}
              className="block glass-card p-4 sm:p-6 hover:scale-[1.02] active:scale-[0.98] transition-transform group touch-manipulation h-full"
            >
              <div className="flex sm:flex-col items-center sm:items-start gap-4 sm:gap-0 h-full">
                <div
                  className={`w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-br ${diff.color} flex items-center justify-center text-2xl sm:text-3xl sm:mb-4 group-hover:scale-110 transition-transform flex-shrink-0`}
                >
                  {diff.emoji}
                </div>
                <div className="flex-1 flex flex-col">
                  <h3 className="text-xl sm:text-2xl font-bold mb-1 sm:mb-2">{diff.name}</h3>
                  <p className="text-gray-500 text-sm sm:text-base flex-1">{diff.description}</p>
                </div>
                <div className="sm:mt-4 flex items-center text-sm text-green-500">
                  <span className="hidden sm:inline">开始挑战 →</span>
                  <svg className="w-5 h-5 sm:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>

      {/* Features */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="mt-8 sm:mt-16 grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 text-center"
      >
        {[
          { icon: '⏱️', title: '计时挑战', desc: '看看你能多快完成' },
          { icon: '💡', title: '智能提示', desc: '卡住时获取帮助' },
          { icon: '📝', title: '笔记功能', desc: '标记候选数字' },
          { icon: '🏆', title: '排行榜', desc: '与其他玩家比拼' },
        ].map((feature, i) => (
          <div key={i} className="p-3 sm:p-4">
            <div className="text-2xl sm:text-4xl mb-1 sm:mb-2">{feature.icon}</div>
            <h4 className="font-semibold text-sm sm:text-base mb-0.5 sm:mb-1">{feature.title}</h4>
            <p className="text-xs sm:text-sm text-gray-500">{feature.desc}</p>
          </div>
        ))}
      </motion.div>
    </div>
  );
}

